import { useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { ArrowLeft, BedDouble, Ruler, Car, MapPin, MessageCircle } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function PropertyDetail() {
  const { id } = useParams();
  const [property, setProperty] = useState(null);
  const [notFound, setNotFound] = useState(false);
  const [active, setActive] = useState(0);

  useEffect(() => {
    window.scrollTo(0, 0);
    setActive(0);
    setProperty(null);
    setNotFound(false);
    axios
      .get(`${API}/properties/${id}`)
      .then((r) => setProperty(r.data))
      .catch(() => setNotFound(true));
  }, [id]);

  if (notFound) {
    return (
      <main className="flex min-h-screen flex-col items-center justify-center gap-6 px-6 pt-24" data-testid="property-not-found">
        <h1 className="font-heading text-3xl font-black tracking-tight text-seed-deep">Imóvel não encontrado</h1>
        <Link to="/#imoveis" className="rounded-full bg-seed-vivid px-8 py-4 font-heading text-sm font-bold text-seed-deep" data-testid="notfound-back">
          Voltar aos imóveis
        </Link>
      </main>
    );
  }

  if (!property) {
    return (
      <main className="flex min-h-screen items-center justify-center pt-24" data-testid="property-loading">
        <div className="h-10 w-10 animate-spin rounded-full border-4 border-seed-deep/10 border-t-seed-salad" />
      </main>
    );
  }

  const waText = encodeURIComponent(
    `Olá! Tenho interesse no imóvel "${property.title}" em ${property.location} (${property.price}). Pode me passar mais detalhes?`
  );

  return (
    <main className="mx-auto max-w-7xl px-6 pt-28 pb-24 lg:px-10" data-testid="property-detail">
      <Link
        to="/#imoveis"
        className="mb-8 inline-flex items-center gap-2 text-sm font-bold text-seed-deep/60 transition-colors hover:text-seed-salad"
        data-testid="detail-back"
      >
        <ArrowLeft size={16} /> Voltar aos imóveis
      </Link>

      <div className="grid grid-cols-1 gap-12 lg:grid-cols-[1.15fr_1fr]">
        <motion.div initial={{ opacity: 0, y: 24 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7, ease: [0.16, 1, 0.3, 1] }}>
          <div className="relative h-[320px] overflow-hidden rounded-lg bg-white shadow-sm sm:h-[480px]">
            <AnimatePresence mode="wait">
              <motion.img
                key={active}
                src={property.images[active]}
                alt={property.title}
                initial={{ opacity: 0, scale: 1.03 }}
                animate={{ opacity: 1, scale: 1 }}
                exit={{ opacity: 0 }}
                transition={{ duration: 0.45 }}
                className="h-full w-full object-cover"
                data-testid="detail-gallery-main"
              />
            </AnimatePresence>
          </div>
          <div className="mt-4 grid grid-cols-4 gap-3">
            {property.images.map((img, i) => (
              <button
                key={i}
                onClick={() => setActive(i)}
                data-testid={`detail-thumb-${i}`}
                className={`h-20 overflow-hidden rounded-md transition-all duration-300 sm:h-24 ${
                  i === active ? "ring-2 ring-seed-vivid ring-offset-2" : "opacity-60 hover:opacity-100"
                }`}
              >
                <img src={img} alt={`${property.title} foto ${i + 1}`} className="h-full w-full object-cover" />
              </button>
            ))}
          </div>
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
        >
          <span
            className="inline-block rounded-full bg-seed-salad/10 px-4 py-1.5 text-xs font-bold uppercase tracking-widest text-seed-salad"
            data-testid="detail-type"
          >
            {property.type === "rural" ? "Imóvel Rural" : "Imóvel Urbano"}
          </span>
          {(property.status === "reservado" || property.status === "vendido") && (
            <span
              className={`ml-3 inline-block rounded-full px-4 py-1.5 text-xs font-bold uppercase tracking-widest ${
                property.status === "vendido" ? "bg-seed-deep text-seed-off" : "bg-seed-vivid text-seed-deep"
              }`}
              data-testid="detail-status"
            >
              {property.status === "vendido" ? "Vendido" : "Reservado"}
            </span>
          )}
          <h1 className="mt-5 font-heading text-4xl font-black tracking-tighter text-seed-deep sm:text-5xl" data-testid="detail-title">
            {property.title}
          </h1>
          <p className="mt-3 flex items-center gap-2 text-base text-seed-deep/60" data-testid="detail-location">
            <MapPin size={17} className="text-seed-salad" /> {property.location}
          </p>
          <p className="mt-6 font-heading text-3xl font-black text-seed-salad sm:text-4xl" data-testid="detail-price">
            {property.price}
          </p>

          <div className="mt-8 grid grid-cols-3 gap-4 border-y border-seed-deep/10 py-6" data-testid="detail-specs">
            <div>
              <BedDouble size={22} className="text-seed-salad" />
              <p className="mt-2 font-heading text-lg font-bold text-seed-deep">{property.beds}</p>
              <p className="text-xs font-semibold uppercase tracking-wider text-seed-deep/50">Quartos</p>
            </div>
            <div>
              <Ruler size={22} className="text-seed-salad" />
              <p className="mt-2 font-heading text-lg font-bold text-seed-deep">{property.area}</p>
              <p className="text-xs font-semibold uppercase tracking-wider text-seed-deep/50">Área</p>
            </div>
            <div>
              <Car size={22} className="text-seed-salad" />
              <p className="mt-2 font-heading text-lg font-bold text-seed-deep">{property.garage}</p>
              <p className="text-xs font-semibold uppercase tracking-wider text-seed-deep/50">Vagas</p>
            </div>
          </div>

          <p className="mt-8 text-base leading-relaxed text-seed-deep/70" data-testid="detail-description">
            {property.description}
          </p>

          <a
            href={`https://wa.me/5534999236969?text=${waText}`}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="detail-interest-whatsapp"
            className="mt-10 flex w-full items-center justify-center gap-3 rounded-full bg-seed-vivid px-8 py-5 font-heading text-lg font-black text-seed-deep transition-transform duration-300 hover:scale-[1.03]"
          >
            <MessageCircle size={22} strokeWidth={2.5} />
            Tenho interesse
          </a>
          <p className="mt-4 text-center text-xs font-semibold uppercase tracking-widest text-seed-deep/40">
            Resposta rápida · 34 99923-6969
          </p>
        </motion.div>
      </div>
    </main>
  );
}
